import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserConfigStore } from '../stores/UserConfigStore';
import { ApiResponseType } from '../functions/APIClient';
import { PaginationType } from '../components/Pagination';

interface UsePaginatedListProps<T, R> {
  fetchFn: (page: number) => Promise<ApiResponseType<R>>;
  getItems: (response: R) => T[] | undefined;
  getPaginate: (response: R) => PaginationType | undefined;
  getItemId: (item: T) => string;
  currentPageFromUrl: number;
  setCurrentPageInUrl: (page: number) => void;
  dependencies: any[];
}

export function usePaginatedList<T, R>({
  fetchFn,
  getItems,
  getPaginate,
  getItemId,
  currentPageFromUrl,
  setCurrentPageInUrl,
  dependencies,
}: UsePaginatedListProps<T, R>) {
  const { userConfig } = useUserConfigStore();
  const isInfinite = (userConfig.pagination_mode ?? 'infinite') === 'infinite';

  const [initialResponse, setInitialResponse] = useState<ApiResponseType<R>>();
  const [items, setItems] = useState<T[]>([]);
  const [pagination, setPagination] = useState<PaginationType | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isFetchingMoreRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const pageToLoad = currentPageFromUrl > 0 ? currentPageFromUrl : 1;
      const res = await fetchFn(pageToLoad);
      if (!isMounted) return;

      setInitialResponse(res);
      const resData = res.data;
      if (resData) {
        setItems(getItems(resData) || []);
        setPagination(getPaginate(resData));
      } else {
        setItems([]);
        setPagination(undefined);
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, currentPageFromUrl]);

  const handleSetPage = useCallback(
    async (nextPage: number) => {
      if (isInfinite) {
        if (isFetchingMoreRef.current) return;
        isFetchingMoreRef.current = true;
        setIsLoadingMore(true);

        try {
          const res = await fetchFn(nextPage);
          const resData = res.data;
          if (resData) {
            const newItems = getItems(resData) || [];
            const newPaginate = getPaginate(resData);

            setItems(prevItems => {
              const existingIds = new Set(prevItems.map(getItemId));
              const filteredNew = newItems.filter(item => !existingIds.has(getItemId(item)));
              return [...prevItems, ...filteredNew];
            });

            if (newPaginate) {
              setPagination(newPaginate);
            }
          }
        } finally {
          isFetchingMoreRef.current = false;
          setIsLoadingMore(false);
        }
      } else {
        setCurrentPageInUrl(nextPage);
      }
    },
    [isInfinite, fetchFn, getItems, getPaginate, getItemId, setCurrentPageInUrl],
  );

  return {
    initialResponse,
    items,
    pagination,
    handleSetPage,
    isLoadingMore,
    isInfinite,
  };
}

export default usePaginatedList;
